import moment, { Moment } from "moment";

import { ArrayUtils, MakeRequired, NullableOptional } from "utils";

import { CropResidueType, HarvestUnitValue } from "consts";

import {
  BulkCropPracticesRequest,
  CROP_TYPE_FCPO,
  CropFCPO,
  CropFCPORequest,
  ENUM_FERTILIZER_UNITS,
  FERTILIZER_TYPE,
  PRACTICE_SLUG,
  PracticeCrop,
  PracticeFCPO,
  PracticeFCPORequest,
  PracticeSlug,
  WithInjectedName,
  ZERO_UUID,
} from "models";

import { RangerPickerValueType } from "components";

import { BulkEditCrop } from "pages/app/all-field-view/components/BulkEditCropDrawer.tsx";
import { getExistingPracticesAppliedStatus } from "pages/app/all-field-view/utils/AllFieldsUtils.ts";
import { getIsSoilPlus3DataCollectionEditable } from "pages/app/all-field-view/utils/dataEntryUtils.ts";

export type BulkCropFormData = NullableOptional<Omit<CropFCPORequest, "seedingStartDate" | "harvestEndDate">> & {
  seedingDateRange?: Array<Moment | null> | null;
  harvestDateRange?: Array<Moment | null> | null;
  showResidue?: boolean;
  showSpeciesCount?: boolean;
};

// Crop
export type BulkAddEditCropFormProps = {
  crop: BulkCropFormData;
  isTillageApplied: boolean | null;
  isMineralApplied: boolean | null;
  isOrganicApplied: boolean | null;
  hasFertilizerManagement: boolean | null;
  [key: TillageFormItemKey]: BulkTillageFormItem;
  [key: MineralFertilizerFormItemKey]: BulkMineralFertilizerFormItem;
  [key: OrganicFertilizerFormItemKey]: BulkOrganicFertilizerFormItem;
};
export type BulkCropValidationError = {
  type: "cropId" | "seedingDate" | "harvestDate" | "hasResidue" | "form";
  message: string;
};
export type CreateBulkCropPracticeRequestBodyProps = {
  data: RequestWithExistingCrop[];
  errors: BulkCropValidationError[];
};

// Tillage
export type BulkTillageFormItem = {
  id: string;
  assignedPracticeId: string;
  type: Extract<PracticeSlug, "mineralFertilizers" | "organicFertilizers" | "tillages">;
  dateRange: RangerPickerValueType;
  tillageDepth: number;
};
export type TillageFormItemKey = `tillage-${number}`;
export type TillageFormItem = {
  [key: TillageFormItemKey]: BulkTillageFormItem;
};
type TillageKeys = keyof Omit<BulkTillageFormItem, "id" | "dateRange">;
type RequiredPracticeFCPORequestTillage = Omit<MakeRequired<PracticeFCPORequest, TillageKeys>, "verificationStatus"> & { id: string };
export const tillageKeysToCompare: (keyof PracticeFCPO)[] = ["assignedPracticeId", "startDate", "endDate", "tillageDepth"];

// Mineral
export type BulkMineralFertilizerFormItem = {
  id: string;
  assignedPracticeId: string;
  type: Extract<PracticeSlug, "mineralFertilizers" | "organicFertilizers" | "tillages">;
  dateRange: RangerPickerValueType;
  amount: number;
};
export type MineralFertilizerFormItemKey = `mineral-${number}`;
export type MineralFertilizerFormItem = {
  [key: MineralFertilizerFormItemKey]: BulkMineralFertilizerFormItem;
};
type MineralFertilizerKeys = keyof Omit<BulkMineralFertilizerFormItem, "id" | "dateRange">;
type RequiredPracticeFCPORequestMineral = Omit<MakeRequired<PracticeFCPORequest, MineralFertilizerKeys>, "verificationStatus"> & { id: string };
export const mineralKeysToCompare: (keyof PracticeFCPO)[] = ["assignedPracticeId", "startDate", "endDate", "amount"];

// Organic
export type BulkOrganicFertilizerFormItem = {
  id: string;
  assignedPracticeId: string;
  type: Extract<PracticeSlug, "mineralFertilizers" | "organicFertilizers" | "tillages">;
  dateRange: RangerPickerValueType;
  applicationType: string;
  amount: number;
  amountUnit?: ENUM_FERTILIZER_UNITS | null;
  nValue: number;
  selfProduced: boolean;
  sanityNoteNitrogen?: string | null;
};
export type OrganicFertilizerFormItemKey = `organic-${number}`;
export type OrganicFertilizerFormItem = {
  [key: OrganicFertilizerFormItemKey]: BulkOrganicFertilizerFormItem;
};
type OrganicFertilizerKeys = keyof Omit<BulkOrganicFertilizerFormItem, "id" | "dateRange">;
type RequiredPracticeFCPORequestOrganic = Omit<MakeRequired<PracticeFCPORequest, OrganicFertilizerKeys>, "verificationStatus"> & { id: string };
export const organicKeysToCompare: (keyof PracticeFCPO)[] = [
  "assignedPracticeId",
  "startDate",
  "endDate",
  "amount",
  "amountUnit",
  "selfProduced",
  "nValue",
  "applicationType",
];

// Genric Practice
export type BulkPracticeValidationError = {
  type: "endDate" | "other";
  message: string;
};
export type CreateBulkPracticeRequestBodyProps = {
  data: PracticeRequestByCropItemId[];
  errors: BulkPracticeValidationError[];
};
export type CreateBulkFertilizerManagementRequestBodyProps = {
  data: FertilizerManagementRequestByCropItemId[];
  errors: BulkPracticeValidationError[];
};
export type BULK_PRACTICE_STATE = "NO_PRACTICES" | "ALL_SAME" | "ONLY_ONE_CROP_WITH_PRACTICES" | "MIXED";
export type PracticeConfigForBulk = {
  practiceToShow: PracticeFCPO[];
  showMessage: boolean;
  toggleState: boolean | null;
  toggleDisabled: boolean;
  bulkPracticeState: BULK_PRACTICE_STATE;
};

// General
export type RequestWithExistingCrop = { bulkCropPracticesRequest: BulkCropPracticesRequest; existingCrop: CropFCPO };
export type PracticeRequestByCropItemId = {
  cropItemId: string;
  practiceRequest: PracticeFCPORequest[];
};
export type FertilizerManagementRequestByCropItemId = {
  cropItemId: string;
  hasFertilizerManagement: boolean | null;
};

type PracticeRequestWithoutVerification = Omit<PracticeFCPORequest, "verificationStatus"> & { id: string };
type CropRequestWithoutVerification = Omit<CropFCPORequest, "seedingVerificationStatus">;

// CROP
export const createBulkCropPracticeRequestBody = (props: {
  cropFormData: BulkCropFormData;
  touchedFormFields: Partial<BulkAddEditCropFormProps>;
  bulkEditCrops: BulkEditCrop[];
  speciesCountThreshold?: number;
}): CreateBulkCropPracticeRequestBodyProps => {
  const { cropFormData, bulkEditCrops, touchedFormFields, speciesCountThreshold } = props;

  if (bulkEditCrops.length === 0) return { data: [], errors: [] };

  const onlyPlaceholders = bulkEditCrops.every((crop) => crop.isPlaceholder);

  const errors: BulkCropValidationError[] = [];
  const cropsRequestList: RequestWithExistingCrop[] = [];

  // Handle only placeholders
  if (onlyPlaceholders) {
    for (const existingCropData of bulkEditCrops) {
      const cropFCPORequest = convertFormCropItemToCropRequestItem(cropFormData, existingCropData.isInRedzone, speciesCountThreshold);
      cropsRequestList.push({
        bulkCropPracticesRequest: {
          crop: {
            ...cropFCPORequest,
            farmFieldCropId: existingCropData.id,
            seedingVerificationStatus: existingCropData.seedingVerificationStatus ?? null,
          },
          practices: [],
          hasFertilizerManagement: null,
          fieldId: existingCropData.fieldId,
        },
        existingCrop: existingCropData,
      });
    }
    return { data: cropsRequestList, errors: errors };
  }

  for (const existingCropData of bulkEditCrops) {
    const cropRequest: CropRequestWithoutVerification | undefined = convertBulkEditCropToCropRequestItem(existingCropData);
    if (existingCropData.isPlaceholder) {
      const cropFCPORequest = convertFormCropItemToCropRequestItem(cropFormData, existingCropData.isInRedzone, speciesCountThreshold);
      cropsRequestList.push({
        bulkCropPracticesRequest: {
          crop: {
            ...cropFCPORequest,
            farmFieldCropId: existingCropData.id,
            seedingVerificationStatus: existingCropData.seedingVerificationStatus ?? null,
          },
          practices: [],
          hasFertilizerManagement: null,
          fieldId: existingCropData.fieldId,
        },
        existingCrop: existingCropData,
      });
      continue; // eslint-disable-line no-continue
    }

    if (!touchedFormFields.crop) {
      cropsRequestList.push({
        bulkCropPracticesRequest: {
          crop: {
            ...cropRequest,
            farmFieldCropId: existingCropData.id,
            seedingVerificationStatus: existingCropData.seedingVerificationStatus ?? null,
          },
          practices: [],
          hasFertilizerManagement: null,
          fieldId: existingCropData.fieldId,
        },
        existingCrop: existingCropData,
      });
      continue; // eslint-disable-line no-continue
    }

    const touchedCropFormFields = touchedFormFields.crop;
    // Apply form data if provided, otherwise use existing data
    if ("cropId" in touchedCropFormFields) {
      if (touchedCropFormFields.cropId) cropRequest.cropId = touchedCropFormFields.cropId;
    }

    if ("seedingDateRange" in touchedCropFormFields) {
      if (touchedCropFormFields.seedingDateRange) {
        if (touchedCropFormFields.seedingDateRange[0]) cropRequest.seedingStartDate = touchedCropFormFields.seedingDateRange[0].toString();
        if (touchedCropFormFields.seedingDateRange[1]) cropRequest.seedingEndDate = touchedCropFormFields.seedingDateRange[1].toString();
      }
    }
    if ("harvestDateRange" in touchedCropFormFields) {
      if (touchedCropFormFields.harvestDateRange) {
        if (touchedCropFormFields.harvestDateRange[0]) cropRequest.harvestStartDate = touchedCropFormFields.harvestDateRange[0].toString();
        if (touchedCropFormFields.harvestDateRange[1]) cropRequest.harvestEndDate = touchedCropFormFields.harvestDateRange[1].toString();
      }
    }

    // Set residue to null if the selected crop doesn’t require a residue
    if (!cropFormData.showResidue) {
      cropRequest.hasResidue = null;
    }

    if ("hasResidue" in touchedCropFormFields) {
      cropRequest.hasResidue = touchedCropFormFields.hasResidue as boolean | null;
    }

    // Set species count to null if the selected crop no longer qualifies for the diverse cover-crop incentive
    if (!cropFormData.showSpeciesCount) {
      cropRequest.speciesCount = null;
    }

    if ("speciesCount" in touchedCropFormFields) {
      cropRequest.speciesCount = touchedCropFormFields.speciesCount ? speciesCountThreshold ?? null : 1;
    }

    if ("sanityNoteYield" in touchedCropFormFields) {
      cropRequest.sanityNoteYield = touchedCropFormFields.sanityNoteYield;
    }

    if ("sanityNoteResidue" in touchedCropFormFields) {
      cropRequest.sanityNoteResidue = touchedCropFormFields.sanityNoteResidue;
    }

    if ("yieldPerHa" in touchedCropFormFields) {
      cropRequest.yieldPerHa = touchedCropFormFields.yieldPerHa;
    }

    if ("yieldPerHaUnit" in touchedCropFormFields) {
      cropRequest.yieldPerHaUnit = touchedCropFormFields.yieldPerHaUnit;
    }

    if ("wasHarvested" in touchedCropFormFields) {
      cropRequest.wasHarvested = touchedCropFormFields.wasHarvested as boolean | null;
      if (touchedCropFormFields.wasHarvested === false) {
        cropRequest.yieldPerHa = null;
        cropRequest.yieldPerHaUnit = null;
      }
    }

    if ("seedingPracticeType" in touchedCropFormFields) {
      cropRequest.seedingPracticeType = touchedCropFormFields.seedingPracticeType;
    }

    if (existingCropData.isInRedzone) {
      if ("mandatory" in touchedCropFormFields) {
        cropRequest.mandatory = touchedCropFormFields.mandatory;
      }
      if ("redzoneExceptionId" in touchedCropFormFields) {
        cropRequest.redzoneExceptionId = touchedCropFormFields.redzoneExceptionId;
      }
      if ("redzoneExceptionExplanation" in touchedCropFormFields) {
        cropRequest.redzoneExceptionExplanation = touchedCropFormFields.redzoneExceptionExplanation;
      }
    }

    cropsRequestList.push({
      bulkCropPracticesRequest: {
        crop: {
          ...cropRequest,
          farmFieldCropId: existingCropData.id,
          seedingVerificationStatus: existingCropData.seedingVerificationStatus ?? null,
        },
        practices: [],
        hasFertilizerManagement: null,
        fieldId: existingCropData.fieldId,
      },
      existingCrop: existingCropData,
    });
  }

  return { data: cropsRequestList, errors: errors };
};

export const convertFormCropItemToCropRequestItem = (
  cropForm: BulkCropFormData,
  isInRedzone?: boolean,
  speciesCountThreshold?: number
): CropRequestWithoutVerification => ({
  cropId: cropForm.cropId!,
  seedingStartDate: cropForm.seedingDateRange![0]!.toISOString(),
  seedingEndDate: cropForm.seedingDateRange![1] ? cropForm.seedingDateRange![1]!.toISOString() : null,
  harvestStartDate: cropForm.harvestDateRange![0] ? cropForm.harvestDateRange![0]!.toISOString() : null,
  harvestEndDate: cropForm.harvestDateRange![1]!.toISOString(),
  hasResidue: cropForm.hasResidue as boolean | null,
  sanityNoteYield: cropForm.sanityNoteYield,
  sanityNoteResidue: cropForm.sanityNoteResidue,
  seedingPracticeType: cropForm.seedingPracticeType,
  yieldPerHa: cropForm.wasHarvested === false ? null : cropForm.yieldPerHa ?? null,
  yieldPerHaUnit: cropForm.wasHarvested === false ? null : cropForm.yieldPerHaUnit ?? null,
  wasHarvested: cropForm.wasHarvested ?? null,
  cropType: cropForm.cropType!,
  mandatory: isInRedzone ? cropForm.mandatory : null,
  redzoneExceptionId: isInRedzone ? cropForm.redzoneExceptionId : null,
  speciesCount: !cropForm.showSpeciesCount ? null : cropForm.speciesCount ? speciesCountThreshold ?? null : 1,
  redzoneExceptionExplanation: isInRedzone ? cropForm.redzoneExceptionExplanation : null,
});

export const convertBulkEditCropToCropRequestItem = (bulkEditCrop: BulkEditCrop): CropRequestWithoutVerification => ({
  cropId: bulkEditCrop.cropId!,
  seedingStartDate: bulkEditCrop.seedingStartDate ? moment(bulkEditCrop.seedingStartDate).toISOString() : null,
  seedingEndDate: bulkEditCrop.seedingEndDate ? moment(bulkEditCrop.seedingEndDate).toISOString() : null,
  harvestStartDate: bulkEditCrop.harvestStartDate ? moment(bulkEditCrop.harvestStartDate).toISOString() : null,
  harvestEndDate: bulkEditCrop.harvestEndDate ? moment(bulkEditCrop.harvestEndDate).toISOString() : null,
  hasResidue: bulkEditCrop.hasResidue,
  sanityNoteYield: bulkEditCrop.sanityNoteYield,
  sanityNoteResidue: bulkEditCrop.sanityNoteResidue,
  seedingPracticeType: bulkEditCrop.seedingPracticeType,
  yieldPerHa: bulkEditCrop.wasHarvested === false ? null : bulkEditCrop.yieldPerHa,
  yieldPerHaUnit: bulkEditCrop.wasHarvested === false ? null : bulkEditCrop.yieldPerHaUnit,
  wasHarvested: bulkEditCrop.wasHarvested ?? null,
  cropType: bulkEditCrop.cropType!,
  mandatory: bulkEditCrop.isInRedzone ? bulkEditCrop.mandatory : null,
  redzoneExceptionId: bulkEditCrop.isInRedzone ? bulkEditCrop.redzoneExceptionId : null,
  speciesCount: bulkEditCrop.speciesCount ?? null,
  redzoneExceptionExplanation: bulkEditCrop.isInRedzone ? bulkEditCrop.redzoneExceptionExplanation : null,
});

/**
 * Gets residue information based on selected crops and display settings.
 *
 * @param params - The parameters object
 * @param selectedItems - Array of bulk edit crops that are selected based on the CropFCPO type
 * @param cropsToDisplay - Array of practice crops available for display
 * @param selectedCropId - ID of the currently selected crop, or null if none selected
 * @param onlyPlaceholderCrops - Whether only placeholder crops are being selected
 * @returns Object containing residue display and validation information
 * @returns showResidue - Whether the residue field should be displayed base on the PracticeCrop.residue
 * @returns isRequired - It’s not required, if we have a mixed value, each crop keeps its value and can be saved as it is.
 * @returns isResidueProduced - The value CropFCPO.hasResidue of exiting crops that the users want to bulk edit
 * @returns hasMixedResidue - Whether selected crops have mixed residue values
 */
export type CropSelectionOption = { id: string; name: string };

// the crops offered for a crop type, alphabetically. Shared by Timeline's BulkEditCropForm and the bulk data
// drawer's crop name section so both offer exactly the same choices
export const getCropSelectionOptions = (cropType: CROP_TYPE_FCPO, cropsToDisplay?: WithInjectedName<PracticeCrop>[]): CropSelectionOption[] => {
  if (!cropsToDisplay) return [];

  const matchesCropType = (crop: WithInjectedName<PracticeCrop>) => {
    if (cropType === CROP_TYPE_FCPO.MAIN) return crop.isMainCrop;
    if (cropType === CROP_TYPE_FCPO.UNDERSOWING) return crop.isUndersowing;
    if (cropType === CROP_TYPE_FCPO.COVER) return crop.isCatchCrop;
    return false;
  };

  return cropsToDisplay
    .filter(matchesCropType)
    .map((crop) => ({ id: crop.id, name: crop.name }))
    .sort((optionA, optionB) => optionA.name?.toString().localeCompare(optionB.name?.toString()));
};

export const getResidueInfo = ({
  selectedItems,
  cropsToDisplay,
  selectedCropId,
  onlyPlaceholderCrops,
}: {
  selectedItems: BulkEditCrop[];
  cropsToDisplay: WithInjectedName<PracticeCrop>[];
  selectedCropId: string | null;
  onlyPlaceholderCrops: boolean;
}): {
  showResidue: boolean;
  isRequired: boolean;
  isResidueProduced: boolean | null;
  residueType: CropResidueType | null;
  residueNote: string | null;
  hasMixedResidue: boolean;
  hasMixedResidueNotes: boolean;
} => {
  const selectedCrop = cropsToDisplay?.find((crop) => crop.id === selectedCropId);

  // Handle placeholder crops: show residue based on the selected crop type
  if (onlyPlaceholderCrops) {
    if (selectedCrop) {
      const showResidue = selectedCrop.residue && !selectedCrop.residueHidden;
      return {
        showResidue,
        isRequired: true,
        isResidueProduced: selectedCrop.residueLeftByDefault,
        residueType: selectedCrop.residueType,
        residueNote: null,
        hasMixedResidue: false,
        hasMixedResidueNotes: false,
      };
    }
    return {
      showResidue: false,
      isRequired: false,
      isResidueProduced: null,
      residueType: null,
      residueNote: null,
      hasMixedResidue: false,
      hasMixedResidueNotes: false,
    };
  }

  // Find crops that require residue information
  const selectedCropsIds = selectedItems.map((crop) => crop.cropId);

  // Do not show residue if any selected crop type has residueHidden set to true or residue set to false
  const anyResidueHidden = selectedCropsIds.some((cropId) => {
    const crop = cropsToDisplay?.find((c) => c.id === cropId);
    return crop?.residueHidden || crop?.residue === false;
  });
  if (!selectedCropId && anyResidueHidden) {
    return {
      showResidue: false,
      isRequired: false,
      isResidueProduced: null,
      residueType: null,
      residueNote: null,
      hasMixedResidue: false,
      hasMixedResidueNotes: false,
    };
  }

  // Derive residueType: return the common type across all selected crops, or null if mixed
  const selectedPracticeCrops = selectedCropsIds
    .map((cropId) => cropsToDisplay?.find((crop) => crop.id === cropId))
    .filter(Boolean) as WithInjectedName<PracticeCrop>[];
  const uniqueResidueTypes = new Set(selectedPracticeCrops.map((crop) => crop.residueType));
  const residueType = uniqueResidueTypes.size === 1 ? uniqueResidueTypes.values().next().value ?? null : null;

  // Derive residueNote: return the common note across all selected items, or null if mixed
  const uniqueResidueNotes = new Set(selectedItems.map((crop) => crop.sanityNoteResidue ?? null));
  const residueNote = uniqueResidueNotes.size === 1 ? uniqueResidueNotes.values().next().value ?? null : null;
  const hasMixedResidueNotes = uniqueResidueNotes.size > 1;

  // Get all the PracticeCrops by the selectedCropsIds that require residue
  const cropsThatNeedResidue = selectedCropsIds
    .map((cropId) => cropsToDisplay?.find((crop) => crop.id === cropId && crop.residue && !crop.residueHidden))
    .filter(Boolean) as WithInjectedName<PracticeCrop>[];

  if (cropsThatNeedResidue.length === 0) {
    if (selectedCrop) {
      return {
        showResidue: selectedCrop.residue && !selectedCrop.residueHidden,
        isRequired: true,
        isResidueProduced: null,
        residueType,
        residueNote,
        hasMixedResidue: false,
        hasMixedResidueNotes,
      };
    }
    return {
      showResidue: false,
      isRequired: false,
      isResidueProduced: null,
      residueType: null,
      residueNote: null,
      hasMixedResidue: false,
      hasMixedResidueNotes,
    };
  }

  //  Get all selectedCropsIds that require residue to check the residue question answer
  const selectedItemsThatNeedResidue = cropsThatNeedResidue.flatMap((cropToDisplay) =>
    selectedItems.filter((selectedCrop) => selectedCrop.cropId === cropToDisplay.id)
  );

  // This is an edge case since each multi-selected CropFCPO should link to a PracticeCrop
  if (selectedItemsThatNeedResidue.length === 0) {
    return {
      showResidue: false,
      isRequired: false,
      isResidueProduced: null,
      residueType: null,
      residueNote: null,
      hasMixedResidue: false,
      hasMixedResidueNotes,
    };
  }

  // Hide residue if the new selection doesn’t require it, even if the multi-selected CropFCPO items have residue
  if (selectedCrop && (!selectedCrop.residue || selectedCrop.residueHidden)) {
    return {
      showResidue: false,
      isRequired: false,
      isResidueProduced: null,
      residueType: null,
      residueNote: null,
      hasMixedResidue: false,
      hasMixedResidueNotes,
    };
  }

  // Mixed Residue answers
  const allHasResidueUnset = selectedItemsThatNeedResidue.every((crop) => crop?.hasResidue === null);
  const allWithResidue = selectedItemsThatNeedResidue.every((crop) => crop?.hasResidue === true);
  const allWithoutResidue = selectedItemsThatNeedResidue.every((crop) => crop?.hasResidue === false);

  if (allHasResidueUnset) {
    return {
      showResidue: true,
      isRequired: true,
      isResidueProduced: selectedCrop?.residueLeftByDefault ?? null,
      residueType,
      residueNote,
      hasMixedResidue: false,
      hasMixedResidueNotes,
    };
  }
  if (allWithResidue) {
    return { showResidue: true, isRequired: true, isResidueProduced: true, residueType, residueNote, hasMixedResidue: false, hasMixedResidueNotes };
  }
  if (allWithoutResidue) {
    return { showResidue: true, isRequired: true, isResidueProduced: false, residueType, residueNote, hasMixedResidue: false, hasMixedResidueNotes };
  }

  // If we have a mixed value, each crop keeps its value, so it’s not required and can be saved as it is.
  return { showResidue: true, isRequired: false, isResidueProduced: null, residueType, residueNote, hasMixedResidue: true, hasMixedResidueNotes };
};

/**
 * Determines the default-checked and mixed-value state of the species-count checkbox based on the crops
 * currently selected for bulk editing that match the resolved crop.
 *
 * @returns defaultChecked - Whether the checkbox should default to checked (all matching crops meet the threshold)
 * @returns hasMixedSpeciesCount - Whether the matching crops disagree on whether the threshold is met
 */
export const getSpeciesCountInfo = ({
  selectedItems,
  selectedCropId,
  threshold,
  onlyPlaceholderCrops,
}: {
  selectedItems: BulkEditCrop[];
  selectedCropId: string | null;
  threshold: number | undefined;
  onlyPlaceholderCrops: boolean;
}): { defaultChecked: boolean; hasMixedSpeciesCount: boolean } => {
  if (onlyPlaceholderCrops || !selectedCropId || !threshold) return { defaultChecked: false, hasMixedSpeciesCount: false };

  const matchingItems = selectedItems.filter((item) => !item.isPlaceholder && item.cropId === selectedCropId);
  if (matchingItems.length === 0) return { defaultChecked: false, hasMixedSpeciesCount: false };

  const checkedStates = matchingItems.map((item) => !!(item.speciesCount && item.speciesCount >= threshold));
  if (checkedStates.every(Boolean)) return { defaultChecked: true, hasMixedSpeciesCount: false };
  if (checkedStates.every((checked) => !checked)) return { defaultChecked: false, hasMixedSpeciesCount: false };
  return { defaultChecked: false, hasMixedSpeciesCount: true };
};

export const getYieldInfo = ({
  selectedItems,
  onlyPlaceholderCrops,
}: {
  selectedItems: BulkEditCrop[];
  onlyPlaceholderCrops: boolean;
}): { valueToDisplay: number | null; hasMixedYieldValues: boolean; valueUnit: HarvestUnitValue | null } => {
  if (onlyPlaceholderCrops) {
    return { valueToDisplay: null, hasMixedYieldValues: false, valueUnit: null };
  }

  const allNonPlaceholderCrops = selectedItems.filter((item) => !item.isPlaceholder);
  if (allNonPlaceholderCrops.length === 0) return { valueToDisplay: null, hasMixedYieldValues: false, valueUnit: null };

  const firstYield = allNonPlaceholderCrops[0].yieldPerHa;
  const allSameYield = allNonPlaceholderCrops.every((item) => item.yieldPerHa === firstYield);

  // The unit is only meaningful when the crops agree on it; a mixed selection has no single unit to show.
  const firstUnit = allNonPlaceholderCrops[0].yieldPerHaUnit ?? null;
  const allSameUnit = allNonPlaceholderCrops.every((item) => (item.yieldPerHaUnit ?? null) === firstUnit);
  const sharedUnit = allSameUnit ? firstUnit : null;

  if (allSameYield && allSameUnit && firstYield !== undefined) {
    return { valueToDisplay: firstYield, hasMixedYieldValues: false, valueUnit: sharedUnit };
  }
  return { valueToDisplay: null, hasMixedYieldValues: true, valueUnit: sharedUnit };
};

export const getDefaultBulkDataEntryDateValues = (selectedItems: BulkEditCrop[] | CropFCPO[]): { seedingDate: Moment; harvestDate: Moment } => {
  const allSeedingStartDates = selectedItems.map((crop) => crop.seedingStartDate ?? crop.fallbackDates.seedingStartDate);
  const allHarvestEndDates = selectedItems.map((crop) => crop.harvestEndDate ?? crop.fallbackDates.harvestEndDate);
  const start = Array.from(new Set(allSeedingStartDates)).sort((a, b) => moment(a).diff(moment(b)))[0];
  const end = Array.from(new Set(allHarvestEndDates)).sort((a, b) => moment(b).diff(moment(a)))[0];

  return { seedingDate: moment(start), harvestDate: moment(end) };
};

const practiceSlugToFormKey = {
  [PRACTICE_SLUG.TILLAGES]: "tillage",
  [PRACTICE_SLUG.MINERAL_FERTILIZERS]: FERTILIZER_TYPE.MINERAL,
  [PRACTICE_SLUG.ORGANIC_FERTILIZERS]: FERTILIZER_TYPE.ORGANIC,
} as const;

// PRACTICES HELPERS
export const getTouchedFormItems = ({
  formData,
  touchedFormFields,
  type,
}: {
  formData: BulkAddEditCropFormProps;
  touchedFormFields: Partial<BulkAddEditCropFormProps>;
  type: PracticeFCPO["type"];
}): { [key: TillageFormItemKey]: BulkTillageFormItem } => {
  const formKey = practiceSlugToFormKey[type];

  const practicesToAdd: { [key: string]: BulkTillageFormItem } = {};
  Object.entries(formData).forEach(([key, data]) => {
    const isTillageKey = key.startsWith(`${formKey}-`);
    if (!isTillageKey) return;
    const isTouched = key in touchedFormFields;
    if (!isTouched) return;
    practicesToAdd[key] = data as BulkTillageFormItem;
  });
  return practicesToAdd;
};

export const convertFormTillageItemToTillageRequestItem = (
  tillageFormItems: TillageFormItem
): { practiceFCPORequestList: RequiredPracticeFCPORequestTillage[]; errors: BulkPracticeValidationError[] } => {
  const practiceFCPORequestList: RequiredPracticeFCPORequestTillage[] = [];
  const errors: BulkPracticeValidationError[] = [];

  Object.entries(tillageFormItems).forEach(([_, data]) => {
    if (!data?.dateRange[1]?.toISOString()) {
      errors.push({ type: "endDate", message: "End date must be provided" });
      return;
    }

    practiceFCPORequestList.push({
      id: data.id,
      type: data.type,
      assignedPracticeId: data.assignedPracticeId,
      startDate: data?.dateRange[0]?.toISOString(),
      endDate: data?.dateRange[1]?.toISOString(),
      tillageDepth: data?.tillageDepth,
    });
  });

  return { practiceFCPORequestList, errors };
};

export const convertFormMineralFertilizerItemToMineralFertilizerRequestItem = (
  mineralFertilizerFormItem: MineralFertilizerFormItem
): { practiceFCPORequestList: RequiredPracticeFCPORequestMineral[]; errors: BulkPracticeValidationError[] } => {
  const practiceFCPORequestList: RequiredPracticeFCPORequestMineral[] = [];
  const errors: BulkPracticeValidationError[] = [];

  Object.entries(mineralFertilizerFormItem).forEach(([_, data]) => {
    if (!data?.dateRange[1]?.toISOString()) {
      errors.push({ type: "endDate", message: "End date must be provided" });
      return;
    }

    practiceFCPORequestList.push({
      id: data.id,
      type: data.type,
      assignedPracticeId: data.assignedPracticeId,
      startDate: data?.dateRange[0]?.toISOString(),
      endDate: data?.dateRange[1]?.toISOString(),
      amount: data?.amount,
    });
  });

  return { practiceFCPORequestList, errors };
};

export const convertFormOrganicFertilizerItemToOrganicFertilizerRequestItem = (
  organicFertilizerFormItem: OrganicFertilizerFormItem
): { practiceFCPORequestList: RequiredPracticeFCPORequestOrganic[]; errors: BulkPracticeValidationError[] } => {
  const practiceFCPORequestList: RequiredPracticeFCPORequestOrganic[] = [];
  const errors: BulkPracticeValidationError[] = [];

  Object.entries(organicFertilizerFormItem).forEach(([_, data]) => {
    if (!data?.dateRange[1]?.toISOString()) {
      errors.push({ type: "endDate", message: "End date must be provided" });
      return;
    }
    practiceFCPORequestList.push({
      id: data.id,
      type: data.type,
      assignedPracticeId: data.assignedPracticeId,
      startDate: data?.dateRange[0]?.toISOString(),
      endDate: data?.dateRange[1]?.toISOString(),
      applicationType: data?.applicationType,
      amount: data?.amount,
      amountUnit: data?.amountUnit ?? null,
      nValue: data.nValue,
      selfProduced: data.selfProduced,
      sanityNoteNitrogen: data.sanityNoteNitrogen ?? null,
    });
  });

  return { practiceFCPORequestList, errors };
};

const getUntouchedTillagesToAdd = (
  untouchedPracticesItems: BulkTillageFormItem[],
  existingPracticesByCropItemId: { [cropItemId: string]: PracticeFCPO[] }
): RequiredPracticeFCPORequestTillage[] => {
  const allExistingPractices = Object.values(existingPracticesByCropItemId).flat();
  const uniquePracticesById = new Map<string, PracticeFCPO>();

  allExistingPractices.forEach((practice) => {
    uniquePracticesById.set(practice.id as string, practice);
  });

  const untouchedPracticesToAdd: RequiredPracticeFCPORequestTillage[] = [];

  uniquePracticesById.forEach((existingPractice) => {
    const existsInUntouchedForm = untouchedPracticesItems.find((untouchedItem) => untouchedItem.id === existingPractice.id);

    if (existsInUntouchedForm) {
      untouchedPracticesToAdd.push({
        id: existingPractice.id as string, // parsing because we know id is defined here
        type: existingPractice.type,
        assignedPracticeId: existingPractice.assignedPracticeId,
        startDate: existingPractice.startDate,
        endDate: existingPractice.endDate,
        tillageDepth: existingPractice.tillageDepth!,
      });
    }
  });

  return untouchedPracticesToAdd;
};

const getUntouchedMineralsToAdd = (
  untouchedPracticesItems: BulkMineralFertilizerFormItem[],
  existingPracticesByCropItemId: { [cropItemId: string]: PracticeFCPO[] }
): RequiredPracticeFCPORequestMineral[] => {
  const allExistingPractices = Object.values(existingPracticesByCropItemId).flat();
  const uniquePracticesById = new Map<string, PracticeFCPO>();

  allExistingPractices.forEach((practice) => {
    uniquePracticesById.set(practice.id as string, practice);
  });

  const untouchedPracticesToAdd: RequiredPracticeFCPORequestMineral[] = [];

  uniquePracticesById.forEach((existingPractice) => {
    const existsInUntouchedForm = untouchedPracticesItems.find((untouchedItem) => untouchedItem.id === existingPractice.id);

    if (existsInUntouchedForm) {
      untouchedPracticesToAdd.push({
        id: existingPractice.id as string, // parsing because we know id is defined here
        type: existingPractice.type,
        assignedPracticeId: existingPractice.assignedPracticeId,
        startDate: existingPractice.startDate,
        endDate: existingPractice.endDate,
        amount: existingPractice.amount!,
      });
    }
  });

  return untouchedPracticesToAdd;
};
const getUntouchedOrganicToAdd = (
  untouchedPracticesItems: BulkOrganicFertilizerFormItem[],
  existingPracticesByCropItemId: { [cropItemId: string]: PracticeFCPO[] }
): RequiredPracticeFCPORequestOrganic[] => {
  const allExistingPractices = Object.values(existingPracticesByCropItemId).flat();
  const uniquePracticesById = new Map<string, PracticeFCPO>();

  allExistingPractices.forEach((practice) => {
    uniquePracticesById.set(practice.id as string, practice);
  });

  const untouchedPracticesToAdd: RequiredPracticeFCPORequestOrganic[] = [];

  uniquePracticesById.forEach((existingPractice) => {
    const existsInUntouchedForm = untouchedPracticesItems.find((untouchedItem) => untouchedItem.id === existingPractice.id);

    if (existsInUntouchedForm) {
      untouchedPracticesToAdd.push({
        id: existingPractice.id as string, // parsing because we know id is defined here
        type: existingPractice.type,
        assignedPracticeId: existingPractice.assignedPracticeId,
        startDate: existingPractice.startDate,
        endDate: existingPractice.endDate,
        amount: existingPractice.amount!,
        amountUnit: existingPractice.amountUnit ?? null,
        applicationType: existingPractice.applicationType!,
        nValue: existingPractice.nValue!,
        selfProduced: existingPractice.selfProduced!,
        sanityNoteNitrogen: existingPractice.sanityNoteNitrogen ?? null,
      });
    }
  });

  return untouchedPracticesToAdd;
};

// PRACTICES MAIN
export const createFertilizerManagementRequest = (props: {
  formData: BulkAddEditCropFormProps;
  bulkEditCrops: BulkEditCrop[];
  touchedFormFields: Partial<BulkAddEditCropFormProps>;
  year: number;
  templateCrop?: BulkEditCrop;
}): CreateBulkFertilizerManagementRequestBodyProps => {
  const { formData, bulkEditCrops, touchedFormFields, year, templateCrop } = props;
  const errors: BulkPracticeValidationError[] = [];
  const data: FertilizerManagementRequestByCropItemId[] = [];

  const isFertilizerManagementTouched = "hasFertilizerManagement" in touchedFormFields;

  // Helper function to find fertilizer management for a crop and year
  const findFertilizerManagement = (crop: BulkEditCrop) =>
    crop.fertilizerManagement?.find((fm) => fm.year === year && fm.fieldCropId === crop.id)?.implements4R ?? null;

  // Handle touched form fields - use form data for all crops
  if (isFertilizerManagementTouched) {
    const touchedData = bulkEditCrops.map((crop) => ({
      cropItemId: crop.id,
      hasFertilizerManagement: formData.hasFertilizerManagement,
    }));
    return { data: touchedData, errors };
  }

  // untouched placeholders have no fertilizer management of their own — fall back to the template crop's value
  const templateFertilizerManagement =
    templateCrop?.fertilizerManagement?.find((fm) => fm.year === templateCrop.year && fm.fieldCropId === templateCrop.id)?.implements4R ?? null;

  // Handle untouched form fields - use existing data
  for (const crop of bulkEditCrops) {
    if (crop.isPlaceholder) {
      data.push({
        cropItemId: crop.id,
        hasFertilizerManagement: templateFertilizerManagement,
      });
    } else {
      data.push({
        cropItemId: crop.id,
        hasFertilizerManagement: findFertilizerManagement(crop),
      });
    }
  }

  return { data, errors };
};

// Generic function to handle all practice types
export const createBulkPracticeRequest = (props: {
  selectedPractices: PracticeFCPO[];
  formData: BulkAddEditCropFormProps;
  bulkEditCrops: BulkEditCrop[];
  touchedFormFields: Partial<BulkAddEditCropFormProps>;
  bulkPracticeState?: BULK_PRACTICE_STATE;
  type: PracticeFCPO["type"];
}): CreateBulkPracticeRequestBodyProps => {
  const { selectedPractices, formData, bulkEditCrops, touchedFormFields, bulkPracticeState, type } = props;

  const onlyPlaceholders = bulkEditCrops.every((crop) => crop.isPlaceholder);
  const bulkPracticeValidationErrors: BulkPracticeValidationError[] = [];
  const practiceRequestByCropItemId: PracticeRequestByCropItemId[] = [];
  const mustHavePractices: PracticeRequestWithoutVerification[] = [];

  // Map existing practices to the corresponding crop
  const existingPracticesByCropItemId = bulkEditCrops.reduce(
    (previousValue, currentValue) => {
      const practices = selectedPractices.filter((selectedPractice) => selectedPractice.cropId === currentValue.id);
      return { ...previousValue, [currentValue.id]: practices };
    },
    {} as { [cropItemId: string]: PracticeFCPO[] }
  );

  // Return the existing practices if mixed
  if (!onlyPlaceholders && bulkPracticeState === "MIXED") {
    // We can return all the existing practices, since we are allowing to edit mixed practices
    Object.entries(existingPracticesByCropItemId).forEach(([cropItemId, practices]) => {
      practiceRequestByCropItemId.push({
        cropItemId: cropItemId,
        practiceRequest: practices as PracticeFCPORequest[],
      });
    });
    return { data: practiceRequestByCropItemId, errors: [] };
  }

  // Get practice applied field name and value based on type
  const practiceAppliedField =
    type === PRACTICE_SLUG.TILLAGES ? "isTillageApplied" : type === PRACTICE_SLUG.MINERAL_FERTILIZERS ? "isMineralApplied" : "isOrganicApplied";
  const isPracticeApplied = formData[practiceAppliedField as keyof BulkAddEditCropFormProps];

  // Handle intentionally "NO" practice applied question
  if (isPracticeApplied === false) {
    const isPracticeQuestionTouched = practiceAppliedField in touchedFormFields;

    // Inject previous verification status
    // Find the corresponding existng practice (based on id or if it has ZERO_UUID (e.g. no fertilizer applied))
    const existingPractice = selectedPractices.find((existingPractice) => existingPractice.type === type && existingPractice.assignedPracticeId === ZERO_UUID);

    // Carry over the dates of the stored placeholder. Sending a fresh timestamp on every save rewrites the
    // practice row even though the answer is still "not applied", which churns its updatedAt for no reason.
    const emptyPracticeStartDate = existingPractice?.startDate ?? moment().toISOString();
    const emptyPracticeEndDate = existingPractice?.endDate ?? moment().toISOString();

    // Create an empty practice based on type
    let emptyPractice: PracticeFCPORequest;
    if (type === PRACTICE_SLUG.TILLAGES) {
      emptyPractice = {
        startDate: emptyPracticeStartDate,
        endDate: emptyPracticeEndDate,
        type: PRACTICE_SLUG.TILLAGES,
        assignedPracticeId: ZERO_UUID,
        verificationStatus: existingPractice?.verificationStatus ?? null,
      };
    } else if (type === PRACTICE_SLUG.MINERAL_FERTILIZERS) {
      emptyPractice = {
        startDate: emptyPracticeStartDate,
        endDate: emptyPracticeEndDate,
        type: PRACTICE_SLUG.MINERAL_FERTILIZERS,
        assignedPracticeId: ZERO_UUID,
        amount: 0,
        verificationStatus: existingPractice?.verificationStatus ?? null,
      };
    } else {
      // organic
      emptyPractice = {
        startDate: emptyPracticeStartDate,
        endDate: emptyPracticeEndDate,
        type: PRACTICE_SLUG.ORGANIC_FERTILIZERS,
        assignedPracticeId: ZERO_UUID,
        amount: 0,
        nValue: 0,
        selfProduced: false,
        applicationType: ZERO_UUID,
        verificationStatus: existingPractice?.verificationStatus ?? null,
      };
    }

    if (isPracticeQuestionTouched) {
      Object.keys(existingPracticesByCropItemId).forEach((cropItemId) => {
        practiceRequestByCropItemId.push({
          cropItemId: cropItemId,
          practiceRequest: [emptyPractice],
        });
      });
    } else {
      // Only add the empty practice to the crops that had them initially or placeholders
      Object.entries(existingPracticesByCropItemId).forEach(([cropItemId, existingPractices]) => {
        const hasZeroId = existingPractices.some((existingPractice) => existingPractice.assignedPracticeId === ZERO_UUID);
        const isPlaceholder = bulkEditCrops.find((crop) => crop.id === cropItemId)?.isPlaceholder;
        practiceRequestByCropItemId.push({
          cropItemId: cropItemId,
          practiceRequest: hasZeroId || isPlaceholder ? [emptyPractice] : [],
        });
      });
    }
    return { data: practiceRequestByCropItemId, errors: bulkPracticeValidationErrors };
  }

  // Checking for touched practices, if touched they must be included in the request
  // This also includes newly added practices
  const touchedFormItems = getTouchedFormItems({ formData, touchedFormFields, type });

  let conversionResult: { practiceFCPORequestList: PracticeRequestWithoutVerification[]; errors: BulkPracticeValidationError[] };
  if (type === PRACTICE_SLUG.TILLAGES) {
    conversionResult = convertFormTillageItemToTillageRequestItem(touchedFormItems);
  } else if (type === PRACTICE_SLUG.MINERAL_FERTILIZERS) {
    conversionResult = convertFormMineralFertilizerItemToMineralFertilizerRequestItem(touchedFormItems);
  } else {
    // organic
    conversionResult = convertFormOrganicFertilizerItemToOrganicFertilizerRequestItem(touchedFormItems);
  }

  const { practiceFCPORequestList: touchedPractices, errors } = conversionResult;
  if (errors.length > 0) {
    bulkPracticeValidationErrors.push(...errors);
  }
  mustHavePractices.push(...touchedPractices);

  // Handle only placeholders
  if (onlyPlaceholders) {
    // untouched — fall back to selectedPractices instead of discarding them
    const practicesToApply = mustHavePractices.length > 0 ? mustHavePractices : (selectedPractices as unknown as PracticeRequestWithoutVerification[]);
    bulkEditCrops.forEach((crop) => {
      practiceRequestByCropItemId.push({
        cropItemId: crop.id,
        practiceRequest: [...practicesToApply] as unknown as PracticeFCPORequest[], // no need to inject verification status here
      });
    });
    return { data: practiceRequestByCropItemId, errors: bulkPracticeValidationErrors };
  }

  // Handle untouched practices
  const untouchedFormItems = Object.fromEntries(Object.entries(formData).filter(([key]) => !(key in touchedFormItems)));
  const untouchedPractices = Object.entries(untouchedFormItems)
    .filter(([key]) => key.startsWith(`${practiceSlugToFormKey[type]}-`))
    .map(([_, item]) => item);

  // Get untouched practices based on type
  let untouchedPracticesToAdd: PracticeRequestWithoutVerification[];
  if (type === PRACTICE_SLUG.TILLAGES) {
    untouchedPracticesToAdd = getUntouchedTillagesToAdd(untouchedPractices as BulkTillageFormItem[], existingPracticesByCropItemId);
  } else if (type === PRACTICE_SLUG.MINERAL_FERTILIZERS) {
    untouchedPracticesToAdd = getUntouchedMineralsToAdd(untouchedPractices as BulkMineralFertilizerFormItem[], existingPracticesByCropItemId);
  } else {
    // organic
    untouchedPracticesToAdd = getUntouchedOrganicToAdd(untouchedPractices as BulkOrganicFertilizerFormItem[], existingPracticesByCropItemId);
  }

  // Add practices to the corresponding crop if they have not been touched and exist in the form
  Object.entries(existingPracticesByCropItemId).forEach(([cropItemId, existingPractices]) => {
    const currentCrop = bulkEditCrops.find((crop) => crop.id === cropItemId);
    if (!currentCrop) {
      bulkPracticeValidationErrors.push({ type: "other", message: "Could not find crop" });
      return;
    }

    // This is only the use case if we have one crop with practices and some others without
    // In this case we do not want to copy over untouched practice items
    if (!currentCrop.isPlaceholder && existingPractices.length === 0) {
      practiceRequestByCropItemId.push({
        cropItemId: cropItemId,
        practiceRequest: [...mustHavePractices] as unknown as PracticeFCPORequest[], // no need to inject verification status here
      });
      return;
    }

    // Inject verification statuses of existing practices
    const injectedPractices: PracticeFCPORequest[] = [...untouchedPracticesToAdd, ...mustHavePractices].map((practice) => {
      // Find the corresponding existng practice based on id
      const existingPractice = existingPractices.find((existingPractice) => existingPractice.id === practice.id);

      return {
        ...practice,
        verificationStatus: existingPractice?.verificationStatus ?? null,
      };
    });

    // Append practices to crop item id
    practiceRequestByCropItemId.push({
      cropItemId: cropItemId,
      practiceRequest: injectedPractices,
    });
  });

  return { data: practiceRequestByCropItemId, errors: bulkPracticeValidationErrors };
};

// GENERAL
export const combineCropsAndPracticeRequest = ({
  crops,
  practices,
  fertilizerManagements,
}: {
  crops: BulkCropPracticesRequest[];
  practices: PracticeRequestByCropItemId[];
  fertilizerManagements: FertilizerManagementRequestByCropItemId[];
}): BulkCropPracticesRequest[] => {
  const dataToSend: BulkCropPracticesRequest[] = crops.reduce((previousValue, currentValue) => {
    const foundPracticesByCrop = practices.filter((practice) => practice.cropItemId === currentValue.crop.farmFieldCropId);
    const foundFertilizerManagement = fertilizerManagements.find(
      (fertilizerManagement) => fertilizerManagement.cropItemId === currentValue.crop.farmFieldCropId
    )?.hasFertilizerManagement;
    const foundPractices = foundPracticesByCrop.flatMap((practice) => practice.practiceRequest);
    return [...previousValue, { ...currentValue, practices: foundPractices, hasFertilizerManagement: foundFertilizerManagement ?? null }];
  }, [] as BulkCropPracticesRequest[]);

  return dataToSend;
};

// Utils

// Currently there is only one type of fertilizer management (4R), but I have set this up to handle multiple in the future
// If undersowing or cover crop, we never want to show the input therefore we don't want to set the existing value
export const getFertilizerManagement = (
  selectedCrops: BulkEditCrop[],
  year: number,
  cropType: CROP_TYPE_FCPO,
  templateCrop?: BulkEditCrop
): { hasMixedFertilizerManagement: boolean; showFertilizerManagement: boolean; fertilizerManagementValue: boolean | undefined } => {
  // Non-main crops don't support fertilizer management
  if (cropType !== CROP_TYPE_FCPO.MAIN) {
    return {
      hasMixedFertilizerManagement: false,
      showFertilizerManagement: false,
      fertilizerManagementValue: undefined,
    };
  }

  // Extract fertilizer management data for the specified year
  const fertilizerManagementData = selectedCrops.map((crop) => crop.fertilizerManagement?.find((fm) => fm.year === year && fm.fieldCropId === crop.id));

  // All crops have no fertilizer management data
  const allUndefined = fertilizerManagementData.every((item) => item === undefined);
  if (allUndefined) {
    // placeholders have no fertilizer management of their own — fall back to the template crop's value
    const templateFertilizerManagement = templateCrop?.fertilizerManagement?.find((fm) => fm.year === templateCrop.year && fm.fieldCropId === templateCrop.id);
    if (templateFertilizerManagement) {
      return {
        hasMixedFertilizerManagement: false,
        showFertilizerManagement: true,
        fertilizerManagementValue: templateFertilizerManagement.implements4R,
      };
    }

    return {
      hasMixedFertilizerManagement: false,
      showFertilizerManagement: false,
      fertilizerManagementValue: undefined,
    };
  }

  // All crops have data - check if values are consistent
  const implements4RValues = fertilizerManagementData.map((item) => item?.implements4R);
  const firstValue = implements4RValues.find((value) => value !== undefined);
  const allSameValue = implements4RValues.every((value) => value === firstValue || value === undefined);

  if (allSameValue) {
    return {
      hasMixedFertilizerManagement: false,
      showFertilizerManagement: true,
      fertilizerManagementValue: firstValue,
    };
  }

  // Mixed values across crops
  return {
    hasMixedFertilizerManagement: true,
    showFertilizerManagement: true,
    fertilizerManagementValue: undefined,
  };
};

export const getToggleStateBasedOnZeroId = (existingPracticesByCropItemId: { [p: string]: PracticeFCPO[] }): boolean | null => {
  const cropIds = Object.keys(existingPracticesByCropItemId);

  // If there are no crops, return true
  if (cropIds.length === 0) {
    return null;
  }
  if (cropIds.length === 1) {
    // This includes practices with ZERO_ID (intential "NO" practice)
    const hasZeroId = Object.entries(existingPracticesByCropItemId).some(([_, item]) => item.some((practice) => practice.assignedPracticeId === ZERO_UUID));
    return !hasZeroId;
  }

  // For multiple crops, check if all crops have the same tillages
  const firstCropPractice = existingPracticesByCropItemId[cropIds[0]];
  const keysToCompare: (keyof PracticeFCPO)[] = ["assignedPracticeId"];

  // Then compare with other crops
  for (let i = 1; i < cropIds.length; i += 1) {
    const currentCropPractice = existingPracticesByCropItemId[cropIds[i]];
    const isEqual = ArrayUtils.arraysEqualByKeys(firstCropPractice, currentCropPractice, keysToCompare);
    if (!isEqual) {
      const isFirstZeroId = firstCropPractice.some((practice) => practice.assignedPracticeId === ZERO_UUID);
      const isCurrentZeroId = currentCropPractice.some((practice) => practice.assignedPracticeId === ZERO_UUID);
      if (isFirstZeroId === isCurrentZeroId) continue; // eslint-disable-line no-continue
      return null;
    }
  }
  const hasZeroId = Object.entries(existingPracticesByCropItemId).some(([_, item]) => item.some((practice) => practice.assignedPracticeId === ZERO_UUID));
  return !hasZeroId;
};

export const checkForSamePracticesAcrossCrops = (
  existingPracticesByCropItemId: { [p: string]: PracticeFCPO[] },
  keysToCompare: (keyof PracticeFCPO)[]
): BULK_PRACTICE_STATE => {
  const cropIds = Object.keys(existingPracticesByCropItemId);

  // If there are no crops, return true
  if (cropIds.length === 0) {
    return "NO_PRACTICES";
  }
  if (cropIds.length === 1) {
    // This includes practices with ZERO_ID (intential "NO" practice)
    return "ONLY_ONE_CROP_WITH_PRACTICES";
  }

  // For multiple crops, check if all crops have the same tillages
  const firstCropTillages = existingPracticesByCropItemId[cropIds[0]];

  // Then compare with other crops
  for (let i = 1; i < cropIds.length; i += 1) {
    const currentCropTillages = existingPracticesByCropItemId[cropIds[i]];

    // Compare length first
    if (firstCropTillages.length !== currentCropTillages.length) {
      return "MIXED";
    }

    // We do not want to compare ZERO_ID since the dates will never be the same
    const isFirstZeroId = firstCropTillages.some((practice) => practice.assignedPracticeId === ZERO_UUID);
    const isCurrentZeroId = currentCropTillages.some((practice) => practice.assignedPracticeId === ZERO_UUID);
    if (isFirstZeroId && isCurrentZeroId) continue; // eslint-disable-line no-continue

    if (!ArrayUtils.arraysEqualByKeys(firstCropTillages, currentCropTillages, keysToCompare)) return "MIXED";
  }
  return "ALL_SAME";
};

export const convertPracticeToPracticeByCropId = (selectedPractices: PracticeFCPO[]) =>
  selectedPractices.reduce(
    (previousValue, currentValue) => {
      const practices = selectedPractices.filter((selectedPractice) => selectedPractice.cropId === currentValue.cropId);
      return { ...previousValue, [currentValue.cropId as string]: practices };
    },
    {} as { [cropItemId: string]: PracticeFCPO[] }
  );
export const getPracticeConfigForBulk = (
  selectedPractices: PracticeFCPO[],
  type: PracticeFCPO["type"],
  lastSubmissionYear: number | null | undefined
): PracticeConfigForBulk | null => {
  const existingPracticesByCropItemId = convertPracticeToPracticeByCropId(selectedPractices);

  let keysToCompare: (keyof PracticeFCPO)[] = [];
  if (type === PRACTICE_SLUG.TILLAGES) {
    keysToCompare = tillageKeysToCompare;
  }
  if (type === PRACTICE_SLUG.MINERAL_FERTILIZERS) {
    keysToCompare = mineralKeysToCompare;
  }
  if (type === PRACTICE_SLUG.ORGANIC_FERTILIZERS) {
    keysToCompare = organicKeysToCompare;
  }
  const bulkPracticeState = checkForSamePracticesAcrossCrops(existingPracticesByCropItemId, keysToCompare);

  // We're going to use the first crops practices, in case they are all the same across crops
  const firstCropsPractices = Object.values(existingPracticesByCropItemId)[0] ?? [];
  const { existingPractices } = getExistingPracticesAppliedStatus(firstCropsPractices);

  const hasZeroUUIDS = firstCropsPractices.some((practice) => practice.assignedPracticeId === ZERO_UUID);
  const hasConfirmedPractices = firstCropsPractices.some(
    (practice) => !!practice.isConfirmed && !getIsSoilPlus3DataCollectionEditable(practice.soilPlus3DataCollection, lastSubmissionYear)
  );

  // This could also be a Practice with ZERO_UUIDS
  if (bulkPracticeState === "ALL_SAME") {
    const toggleState = !hasZeroUUIDS;
    return { practiceToShow: existingPractices, showMessage: false, toggleDisabled: hasConfirmedPractices, toggleState: toggleState, bulkPracticeState };
  }
  if (bulkPracticeState === "NO_PRACTICES") {
    return { practiceToShow: existingPractices, showMessage: false, toggleDisabled: false, toggleState: null, bulkPracticeState };
  }
  if (bulkPracticeState === "ONLY_ONE_CROP_WITH_PRACTICES") {
    const toggleState = !hasZeroUUIDS;
    return { practiceToShow: existingPractices, showMessage: false, toggleDisabled: hasConfirmedPractices, toggleState: toggleState, bulkPracticeState };
  }
  if (bulkPracticeState === "MIXED") {
    const toggleState = getToggleStateBasedOnZeroId(existingPracticesByCropItemId);
    return { practiceToShow: [], showMessage: true, toggleDisabled: true, toggleState: toggleState, bulkPracticeState };
  }

  return null;
};

export type RedZoneQuestionInfoForForm = {
  isQuestionRequiredForSaving: boolean;
  isAnswerRequiredForSaving: boolean;
  hasMixedRedZoneQuestion: boolean;
  hasMixedRedZoneAnswer: boolean;
  redZoneQuestion: boolean | null;
  redZoneExceptionAnswer: string | null;
  hasMixedRedZoneExplanation: boolean;
  redZoneExceptionExplanation: string | null;
};

/**
 * Helper to create RedZoneQuestionInfoForForm objects with defaults
 */
const createRedZoneInfo = (overrides: Partial<RedZoneQuestionInfoForForm> = {}): RedZoneQuestionInfoForForm => ({
  isQuestionRequiredForSaving: false,
  isAnswerRequiredForSaving: false,
  hasMixedRedZoneQuestion: false,
  hasMixedRedZoneAnswer: false,
  redZoneQuestion: null,
  redZoneExceptionAnswer: null,
  hasMixedRedZoneExplanation: false,
  redZoneExceptionExplanation: null,
  ...overrides,
});

/**
 * Determines the common value across crops if all values are equal, otherwise returns null
 */
const getCommonValue = <T>(crops: BulkEditCrop[], getValue: (crop: BulkEditCrop) => T): T | null => {
  if (crops.length === 0) return null;
  const firstValue = getValue(crops[0]);
  const allEqual = crops.every((crop) => getValue(crop) === firstValue);
  return allEqual ? firstValue : null;
};

/**
 * Checks if all values are the same across crops
 */
const hasUniformValues = <T>(crops: BulkEditCrop[], getValue: (crop: BulkEditCrop) => T): boolean => {
  if (crops.length === 0) return true;
  const firstValue = getValue(crops[0]);
  return crops.every((crop) => getValue(crop) === firstValue);
};

export const getRedZoneQuestionInfoForForm = (selectedItems: BulkEditCrop[]): RedZoneQuestionInfoForForm => {
  const onlyPlaceholders = selectedItems.every((item) => item.isPlaceholder);
  const hasFieldWithRedZone = selectedItems.some((crop) => crop.isInRedzone);

  // No redzone fields - nothing required
  if (!hasFieldWithRedZone) {
    return createRedZoneInfo();
  }

  // Only placeholders - require both question and answer
  if (onlyPlaceholders) {
    return createRedZoneInfo({
      isQuestionRequiredForSaving: true,
      isAnswerRequiredForSaving: true,
    });
  }

  // Filter to get non-placeholder crops in redzone
  const nonPlaceholderCropsInRedZone = selectedItems.filter((item) => !item.isPlaceholder && item.isInRedzone);

  // Handle mix: placeholders in redzone but no existing crops in redzone
  if (nonPlaceholderCropsInRedZone.length === 0) {
    return createRedZoneInfo({
      isQuestionRequiredForSaving: true,
      isAnswerRequiredForSaving: true,
    });
  }

  // Check for mixed values
  const hasMixedMandatory = !hasUniformValues(nonPlaceholderCropsInRedZone, (crop) => crop.mandatory);
  const hasMixedExceptionId = !hasUniformValues(nonPlaceholderCropsInRedZone, (crop) => crop.redzoneExceptionId);
  const hasMixedExplanation = !hasUniformValues(nonPlaceholderCropsInRedZone, (crop) => crop.redzoneExceptionExplanation);

  // Early return: all answered "Yes" to mandatory question
  if (nonPlaceholderCropsInRedZone.every((crop) => crop.mandatory === true)) {
    return createRedZoneInfo({
      hasMixedRedZoneQuestion: false,
      hasMixedRedZoneAnswer: false,
      redZoneQuestion: true,
    });
  }

  // Determine common values for the form (if uniform)
  const commonMandatory = getCommonValue(nonPlaceholderCropsInRedZone, (crop) => crop.mandatory);
  const commonExceptionId = getCommonValue(nonPlaceholderCropsInRedZone, (crop) => crop.redzoneExceptionId);
  const commonExplanation = getCommonValue(nonPlaceholderCropsInRedZone, (crop) => crop.redzoneExceptionExplanation);

  // Check for incomplete data
  const hasUnansweredQuestions = nonPlaceholderCropsInRedZone.some((crop) => crop.mandatory === null);
  const hasNoWithoutAnswer = nonPlaceholderCropsInRedZone.some((crop) => crop.mandatory === false && crop.redzoneExceptionId === null);

  // Require input if there are any incomplete answers
  if (hasUnansweredQuestions || hasNoWithoutAnswer) {
    return createRedZoneInfo({
      isQuestionRequiredForSaving: hasUnansweredQuestions,
      isAnswerRequiredForSaving: true,
      hasMixedRedZoneQuestion: hasMixedMandatory,
      hasMixedRedZoneAnswer: hasMixedExceptionId,
      redZoneQuestion: commonMandatory,
      redZoneExceptionAnswer: commonExceptionId,
      hasMixedRedZoneExplanation: hasMixedExplanation,
      redZoneExceptionExplanation: commonExplanation,
    });
  }

  // All data is complete
  return createRedZoneInfo({
    hasMixedRedZoneQuestion: hasMixedMandatory,
    hasMixedRedZoneAnswer: hasMixedExceptionId,
    redZoneQuestion: commonMandatory,
    redZoneExceptionAnswer: commonExceptionId,
    hasMixedRedZoneExplanation: hasMixedExplanation,
    redZoneExceptionExplanation: commonExplanation,
  });
};
